import clsx from "clsx";
import { forwardRef, useId } from "react";

interface ToggleProps
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
	checked: boolean;
	onChange: (checked: boolean) => void;
	label?: string;
	labelPosition?: "left" | "right";
}

const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
	(
		{
			checked,
			onChange,
			disabled,
			label,
			labelPosition = "left",
			className,
			id: externalId,
			...props
		},
		ref,
	) => {
		const autoId = useId();
		const id = externalId ?? autoId;

		const toggle = (
			<button
				ref={ref}
				id={id}
				type="button"
				role="switch"
				aria-checked={checked}
				aria-label={!label ? props["aria-label"] : undefined}
				disabled={disabled}
				onClick={() => !disabled && onChange(!checked)}
				className={clsx(
					"relative inline-flex h-20 w-40 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200",
					checked ? "bg-primary-500" : "bg-grayscale-300",
					disabled && "cursor-not-allowed opacity-50",
					!label && className,
				)}
				{...props}
			>
				<span
					className={clsx(
						"pointer-events-none inline-block h-16 w-16 rounded-full bg-white shadow transition-transform duration-200",
						checked ? "translate-x-20" : "translate-x-2",
					)}
				/>
			</button>
		);

		if (!label) return toggle;

		return (
			<div
				className={clsx(
					"inline-flex items-center gap-8",
					labelPosition === "right" && "flex-row-reverse",
					className,
				)}
			>
				<label
					htmlFor={id}
					className={clsx(
						"text-sm font-medium text-grayscale-800 select-none cursor-pointer",
						disabled && "cursor-not-allowed opacity-50",
					)}
				>
					{label}
				</label>
				{toggle}
			</div>
		);
	},
);

Toggle.displayName = "Toggle";

export default Toggle;
